import { Controller } from 'react-hook-form';
import { IEvent } from '@clickersphere/core/entities/IEvent';
import { Button } from '../../Button';
import DateInput from '../../DateInput';
import { Input } from '../../Input';
import LocationCombobox from '../../Location/LocationCombobox';
import Hint from '../../Hint';
import Select from '../../Select';
import TimePicker from '../../TimePicker';
import { useRegisterEventFormController } from './useRegisterEventFormController';

interface IEventDraft {
  leadId?: string;
  statusId?: string;
  description?: string;
  locationId?: string | null;
}

interface IEventFormProps {
  event?: IEvent;
  leadId?: string;
  eventStatusId?: string;
  eventDraft?: IEventDraft;
}

export default function EventForm({
  event,
  leadId,
  eventStatusId,
  eventDraft,
}: IEventFormProps) {
  const {
    isFetching,
    errors,
    control,
    t,
    leadOptions,
    eventStatus,
    checklistOptions,
    isEditing,
    isTimePickerVisible,
    birthPhotographerMode,
    birthOptions,
    imageConsentOptions,
    dateField,
    handleSubmit,
    register,
    toggleTimePickerVisibility,
    handleFormReset,
    setSelectedLocationName,
  } = useRegisterEventFormController({
    event,
    leadId,
    eventStatusId,
    eventDraft,
  });

  return (
    <form
      className="my-4 flex w-full flex-col items-center gap-4"
      onSubmit={handleSubmit}
    >
      <Controller
        name="leadId"
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            placeholder={t('register.fields.lead')}
            options={leadOptions}
            searchable
            searchPlaceholder={t('register.fields.leadSearchPlaceholder')}
            searchNoResultsText={t('register.fields.leadNoResults')}
            error={error?.message}
            onChange={onChange}
            disabled={isFetching || !!leadId || !!event?.leadId}
            value={value}
          />
        )}
      />

      <Input
        type="text"
        placeholder={t('register.fields.description')}
        {...register('description')}
        error={errors.description?.message}
        disabled={isFetching}
      />

      <Controller
        name="statusId"
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            placeholder={t('register.fields.status')}
            options={eventStatus.map((s) => ({
              value: s.id,
              text: s.name,
            }))}
            error={error?.message}
            onChange={onChange}
            disabled={isFetching}
            value={value}
          />
        )}
      />

      <div className="flex w-full items-center gap-2">
        <Controller
          control={control}
          name="date"
          render={({
            field: { value, name, onChange },
            fieldState: { error },
          }) => (
            <DateInput
              name={name}
              value={value ?? ''}
              onChange={onChange}
              placeholder={t('register.fields.date')}
              error={error?.message}
              disabled={isFetching}
            />
          )}
        />

        <Controller
          name="time"
          control={control}
          render={({
            field: { name, value, onChange },
            fieldState: { error },
          }) => (
            <Input
              name={name}
              disabled
              onChange={(e) => {
                onChange(e.target.value);
              }}
              value={value ?? ''}
              placeholder={t('register.fields.time')}
              error={error?.message}
            >
              <TimePicker
                className="absolute top-3 left-full -translate-x-10 cursor-pointer"
                disabled={dateField.empty || dateField.invalid}
                open={isTimePickerVisible}
                toggleVisibility={toggleTimePickerVisibility}
                onSelect={onChange}
                onCancel={toggleTimePickerVisibility}
                value={value}
              />
            </Input>
          )}
        />
      </div>

      <Controller
        name="locationId"
        control={control}
        render={({ field: { value, onChange } }) => (
          <LocationCombobox
            value={value}
            onChange={(locationId, locationName) => {
              onChange(locationId);
              setSelectedLocationName(locationName);
            }}
            placeholder={t('register.fields.location')}
            disabled={isFetching}
          />
        )}
      />

      {birthPhotographerMode && birthOptions.length > 0 && (
        <Controller
          name="birthId"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              placeholder={t('birth.event.field')}
              options={birthOptions}
              emptyOption
              emptyOptionLabel={t('birth.event.none')}
              value={value ?? ''}
              onChange={onChange}
              error={error?.message}
              disabled={isFetching}
            />
          )}
        />
      )}

      <div className="w-full space-y-1.5">
        <Controller
          name="imageConsent"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              placeholder={t('event.imageConsent.field')}
              options={imageConsentOptions}
              value={value}
              onChange={onChange}
              error={error?.message}
              disabled={isFetching}
            />
          )}
        />
        <Hint>{t('event.imageConsent.recordOnlyHint')}</Hint>
      </div>

      {!isEditing && checklistOptions.length > 0 && (
        <Controller
          name="checklistId"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              placeholder={t('register.fields.checklist')}
              options={checklistOptions}
              emptyOption
              emptyOptionLabel={t('register.fields.checklistNone')}
              value={value ?? ''}
              onChange={onChange}
              error={error?.message}
              disabled={isFetching}
            />
          )}
        />
      )}

      <section className="mt-4 flex w-full gap-4">
        <Button
          className="w-50"
          labelPath="register.clearFields"
          disabled={isFetching}
          color="ghost"
          onClick={() => handleFormReset()}
        />
        <Button
          labelPath="register.submit"
          type="submit"
          disabled={isFetching}
          isLoading={isFetching}
        />
      </section>
    </form>
  );
}
